import type { GETConnection } from '~/services/apiClient'
import { ConnectionValidationStatus } from '~/services/apiClient'

/**
 * Plain-language UX status for connections.
 * Maps from the backend's ConnectionValidationStatus to user-facing vocabulary.
 */
export type UXConnectionStatus =
  'connected' | 'failing' | 'not_set_up' | 'paused' | 'not_used'

/**
 * Derives the user-facing UX status from a backend connection object.
 * When no connection exists (never set up), returns 'not_set_up'.
 */
export function getUXStatus(
  connection?: GETConnection | null
): UXConnectionStatus {
  if (!connection) return 'not_set_up'

  switch (connection.validation_status) {
    case ConnectionValidationStatus.Validated:
      return 'connected'
    case ConnectionValidationStatus.Invalid:
      return 'failing'
    case ConnectionValidationStatus.Disabled:
      return 'paused'
    case ConnectionValidationStatus.NotUsed:
      return 'not_used'
    case ConnectionValidationStatus.Deleted:
      // Deleted connections should be filtered upstream, but guard defensively
      return 'not_used'
    case ConnectionValidationStatus.Unvalidated:
      return 'not_set_up'
    default:
      return 'not_set_up'
  }
}

export const STATUS_LABEL: Record<UXConnectionStatus, string> = {
  connected: 'Connected',
  failing: 'Failing',
  not_set_up: 'Not set up',
  paused: 'Paused',
  not_used: 'Not used',
}

export type StatusTone = 'ok' | 'err' | 'warn' | 'neutral'

export const STATUS_TONE: Record<UXConnectionStatus, StatusTone> = {
  connected: 'ok',
  failing: 'err',
  not_set_up: 'warn',
  paused: 'neutral',
  not_used: 'neutral',
}
